import { Link, useParams } from 'react-router-dom';
import { useSession } from '../lib/session';
import { Alert, Card } from '../components/ui';
import { useT } from '../i18n/locale';

/** `live` marks the actions that need a working Accurate connection. */
const ITEMS = [
  { to: 'scan', icon: '▤', key: 'menu.scan', tone: 'text-brand-500', live: true },
  { to: 'drafts', icon: '✎', key: 'menu.drafts', tone: 'text-amber-500', live: true },
  { to: 'history', icon: '☰', key: 'menu.history', tone: 'text-emerald-500', live: false },
] as const;

export default function MenuScreen() {
  const { databaseId } = useParams<{ databaseId: string }>();
  const { databases } = useSession();
  const t = useT();
  const db = databases.find((d) => d.id === databaseId);

  if (!db) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Alert tone="warn">{t('menu.notFound')}</Alert>
      </div>
    );
  }

  /*
   * A disconnected database keeps its receiving history and nothing else.
   * Offering Scan here would take the operator through a whole count before
   * failing at the PO lookup, so the actions that need Accurate are not shown
   * at all rather than shown and refused.
   */
  const items = ITEMS.filter((item) => db.isConnected || !item.live);

  return (
    <div className="mx-auto w-full max-w-5xl p-4">
      <h2 className="mb-4 text-lg font-bold text-white">{db.alias}</h2>
      {!db.isConnected ? (
        <Alert tone="warn">{t('menu.disconnected')}</Alert>
      ) : null}
      <div className="grid gap-4 md:grid-cols-3">
        {items.map((item) => (
          <Link key={item.to} to={`/db/${databaseId}/${item.to}`} className="no-underline">
            <Card className="flex min-h-40 flex-col items-center justify-center p-6 transition-all hover:-translate-y-1 hover:border-brand-500 hover:shadow-lg">
              <div className={`mb-3 text-4xl ${item.tone}`}>{item.icon}</div>
              <h3 className="m-0 text-base font-bold text-slate-800">{t(item.key)}</h3>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
